import type {
	CampaignRequestEvent,
	DocumentKind,
	OutcomeSource,
	PriceCode,
	PriceUnit,
	QuoteLine,
	RequestAgentKind,
	RequestBlocker,
	RequestObjective,
	RequestState,
} from "@/hooks/api/use-campaign-requests";

import { labelOf } from "./humanise";

/**
 * Labels, colours and formatters shared by every campaign-request screen.
 *
 * Two things in here are mirrors of the API and must not be improved on: `LEGAL_TRANSITIONS`
 * copies `REQUEST_TRANSITIONS` and `isFrozen` copies `FROZEN_STATES`, both from
 * `src/modules/campaign-requests/types/transitions.ts`. A mirror that drifts is worse than no
 * mirror — it offers moves that 422 and hides the ones that work — so change them only when the
 * server file changes, and change them to match it exactly.
 */

type ChipColor = "default" | "primary" | "secondary" | "info" | "success" | "warning" | "error";

// ---------------------------------------------------------------------------------------------
// Markets and languages
// ---------------------------------------------------------------------------------------------

export interface MarketOption {
	/** ISO 3166-1 alpha-2. */
	value: string;
	label: string;
	flag: string;
	/**
	 * The IANA zone a new market row starts from. Every time on a market schedule is local to it,
	 * and the API has no default: one 09:00–13:00 window shared across four markets dials three of
	 * them at the wrong local hour.
	 */
	timezone: string;
	/** What the client reads beside a time — "09:00–13:00 SGT". */
	clock: string;
}

export const MARKETS: MarketOption[] = [
	{ value: "SG", label: "Singapore", flag: "🇸🇬", timezone: "Asia/Singapore", clock: "SGT" },
	{ value: "AU", label: "Australia", flag: "🇦🇺", timezone: "Australia/Sydney", clock: "AEST" },
	{ value: "MY", label: "Malaysia", flag: "🇲🇾", timezone: "Asia/Kuala_Lumpur", clock: "MYT" },
	{ value: "TH", label: "Thailand", flag: "🇹🇭", timezone: "Asia/Bangkok", clock: "ICT" },
	{ value: "ID", label: "Indonesia", flag: "🇮🇩", timezone: "Asia/Jakarta", clock: "WIB" },
];

const MARKETS_BY_CODE = new Map(MARKETS.map((market) => [market.value, market]));

export function marketOption(code: string | null | undefined): MarketOption | undefined {
	return code ? MARKETS_BY_CODE.get(code) : undefined;
}

export function marketLabel(code: string | null | undefined): string {
	if (!code) return "—";
	return MARKETS_BY_CODE.get(code)?.label ?? code;
}

export function marketFlag(code: string | null | undefined): string {
	return marketOption(code)?.flag ?? "";
}

/**
 * The zone a market row should start from. Falls back to Singapore rather than to the browser's
 * clock: a wrong-but-stated zone is visible on the card, a silently local one is not.
 */
export function marketTimezone(code: string | null | undefined): string {
	return marketOption(code)?.timezone ?? "Asia/Singapore";
}

/** The short clock name shown beside a local time, e.g. "AEST". Empty where we have no short form. */
export function marketClock(code: string | null | undefined): string {
	return marketOption(code)?.clock ?? "";
}

export interface LanguageOption {
	/** BCP 47. The API accepts any well-formed tag; these are the ones we run today. */
	value: string;
	label: string;
	nativeLabel: string;
}

export const LANGUAGES: LanguageOption[] = [
	{ value: "en", label: "English", nativeLabel: "English" },
	{ value: "ms-MY", label: "Bahasa Melayu", nativeLabel: "Bahasa Melayu" },
	{ value: "th", label: "Thai", nativeLabel: "ไทย" },
	{ value: "id", label: "Bahasa Indonesia", nativeLabel: "Bahasa Indonesia" },
	{ value: "zh-Hans-SG", label: "Mandarin (Simplified)", nativeLabel: "简体中文" },
	{ value: "ta-SG", label: "Tamil", nativeLabel: "தமிழ்" },
];

const LANGUAGES_BY_TAG = new Map(LANGUAGES.map((language) => [language.value, language]));

export function languageLabel(tag: string | null | undefined): string {
	if (!tag) return "—";
	return LANGUAGES_BY_TAG.get(tag)?.label ?? tag;
}

// ---------------------------------------------------------------------------------------------
// States, blockers and whose move it is
// ---------------------------------------------------------------------------------------------

export const REQUEST_STATE_LABELS: Record<RequestState, string> = {
	draft: "Draft",
	submitted: "Submitted",
	quoted: "Quoted",
	awaiting_confirmation: "Awaiting confirmation",
	confirmed: "Confirmed",
	in_preparation: "In preparation",
	ready: "Ready",
	running: "Running",
	completed: "Completed",
	changes_requested: "Changes requested",
	cancelled: "Cancelled",
};

/**
 * The same states in the client's language rather than ours — UX §12 note 1: "The timeline is
 * written in their language, not ours … The state machine is behind it; the copy isn't the state
 * name." Use these on client surfaces, `REQUEST_STATE_LABELS` on admin ones.
 */
export const REQUEST_STATE_CLIENT_LABELS: Record<RequestState, string> = {
	draft: "Draft",
	submitted: "Being priced",
	quoted: "Quote being prepared",
	awaiting_confirmation: "Awaiting your confirmation",
	confirmed: "Confirmed",
	in_preparation: "In preparation",
	ready: "Ready to start",
	running: "Running",
	completed: "Completed",
	changes_requested: "Changes requested",
	cancelled: "Cancelled",
};

export const REQUEST_STATE_OPTIONS = Object.entries(REQUEST_STATE_LABELS).map(([value, label]) => ({
	value: value as RequestState,
	label,
}));

export const REQUEST_BLOCKER_LABELS: Record<RequestBlocker, string> = {
	leads_missing: "Lead list",
	flow_missing: "Call flow",
	quote_unconfirmed: "Quote confirmation",
	sow_missing: "Statement of work",
	po_missing: "Purchase order",
	voice_request: "Voice request",
};

/**
 * Why a blocker is open, in the words the client's own list uses. All but one of these is
 * something only the client can clear, which is what makes an outstanding blocker mean "waiting on
 * them" — `voice_request` is the exception and says so, because it is ours to answer.
 */
export const REQUEST_BLOCKER_HELP: Record<RequestBlocker, string> = {
	leads_missing: "No lead list has been uploaded yet. You can send it later — we quote and build in the meantime.",
	flow_missing: "You asked us to build or update an agent, and no call flow has been attached to it yet.",
	quote_unconfirmed: "Confirm the quote once it has been issued.",
	sow_missing: "Attach the statement of work, or tell us its reference.",
	po_missing: "Attach the purchase order, or tell us its reference.",
	voice_request: "You asked for voices beyond the ones on offer. We are sourcing them — nothing is needed from you.",
};

/** Blockers never stop a request progressing, so they are amber and never red. */
export const REQUEST_BLOCKER_COLORS: Record<RequestBlocker, ChipColor> = {
	leads_missing: "warning",
	flow_missing: "warning",
	quote_unconfirmed: "warning",
	sow_missing: "warning",
	po_missing: "warning",
	/** Ours to clear, not theirs — see `blockedOnFor` on the API. Accent rather than amber. */
	voice_request: "primary",
};

export const REQUEST_OBJECTIVE_LABELS: Record<RequestObjective, string> = {
	discovery_qualification: "Discovery & qualification",
	appointment_setting: "Appointment setting",
	lead_reactivation: "Lead reactivation",
	event_invitation: "Event invitation",
	renewal_retention: "Renewal & retention",
	research: "Research",
	other: "Other",
};

export const REQUEST_OBJECTIVE_OPTIONS = Object.entries(REQUEST_OBJECTIVE_LABELS).map(([value, label]) => ({
	value: value as RequestObjective,
	label,
}));

export function objectiveLabel(objective: RequestObjective | string | null | undefined): string {
	return labelOf(REQUEST_OBJECTIVE_LABELS, objective);
}

// ---------------------------------------------------------------------------------------------
// Agent choices — copy taken from UX §05 and the architecture canvas §03
// ---------------------------------------------------------------------------------------------

export interface AgentKindOption {
	value: RequestAgentKind;
	/** What the option is called on the card the client picks from. */
	label: string;
	/** The one line under it. Canvas copy — this is what the client reads before choosing. */
	help: string;
	/** Whether choosing this means a document has to be attached before go-live. */
	needsCallFlow: boolean;
	/** The price code the choice triggers, or null where there is nothing to bill. */
	priceCode: PriceCode | null;
	/** What the option's price chip says when no rate is known yet. */
	priceHint: string;
}

export const AGENT_KIND_OPTIONS: AgentKindOption[] = [
	{
		value: "agent_setup",
		label: "Build a new agent",
		help: "Upload your call flow and we'll build it.",
		needsCallFlow: true,
		priceCode: "agent_setup",
		priceHint: "New agent setup",
	},
	{
		value: "reuse_existing",
		label: "Reuse one of my agents",
		help: "Pick from agents already built for you.",
		needsCallFlow: false,
		// Reuse has no price code rather than a zero-rated one: the absence of a code is the
		// decision itself, where a rate of zero is a price book row somebody can edit by accident.
		priceCode: null,
		priceHint: "no charge",
	},
	{
		value: "regional_deployment",
		label: "Reuse an agent in a new market",
		help: "An agent you already run, deployed into another market. Same script, new local number.",
		needsCallFlow: false,
		priceCode: "regional_deployment",
		priceHint: "Deploy to new market",
	},
	{
		value: "translated_agent",
		label: "Same script, translated",
		help: "We translate and localise the English flow. Reviewed by a native speaker before it dials.",
		needsCallFlow: false,
		priceCode: "translated_agent",
		priceHint: "Translated agent",
	},
	{
		value: "agent_revision",
		label: "Update an existing agent",
		help: "Upload a revised call flow.",
		needsCallFlow: true,
		priceCode: "agent_revision",
		priceHint: "Agent revision",
	},
];

const AGENT_KINDS_BY_VALUE = new Map(AGENT_KIND_OPTIONS.map((option) => [option.value, option]));

export function agentKindLabel(kind: RequestAgentKind | null | undefined): string {
	if (!kind) return "—";
	return AGENT_KINDS_BY_VALUE.get(kind)?.label ?? kind;
}

/** The two choices that only mean anything with a call flow attached to them. */
export function agentKindNeedsCallFlow(kind: RequestAgentKind): boolean {
	return kind === "agent_setup" || kind === "agent_revision";
}

// ---------------------------------------------------------------------------------------------
// Prices
// ---------------------------------------------------------------------------------------------

export const PRICE_CODE_LABELS: Record<PriceCode, string> = {
	agent_setup: "New agent setup",
	translated_agent: "Translated agent",
	regional_deployment: "Deploy to new market",
	agent_revision: "Agent revision",
	number_rental: "Local number",
	call_time: "Call time",
	carrier_rate: "Carrier rate",
	minimum_charge: "Minimum charge",
	reuse_existing: "Reuse an existing agent",
};

/** How each charge is measured, in the price book's own words (UX §16). */
export const PRICE_CODE_UNIT_HINTS: Record<PriceCode, string> = {
	agent_setup: "per agent",
	translated_agent: "per language",
	regional_deployment: "per agent / market",
	agent_revision: "per revision",
	number_rental: "per number / month",
	call_time: "per minute · billed per second",
	carrier_rate: "per minute / market · passed through at cost",
	minimum_charge: "per campaign",
	reuse_existing: "no charge",
};

export const PRICE_UNIT_LABELS: Record<PriceUnit, string> = {
	each: "Each",
	per_month: "Per month",
	per_minute: "Per minute",
};

export const PRICE_UNIT_OPTIONS = Object.entries(PRICE_UNIT_LABELS).map(([value, label]) => ({
	value: value as PriceUnit,
	label,
}));

/**
 * A quote reads as three groups, not two (UX §10).
 *
 * Carrier termination is its own client-facing line rather than part of our per-minute rate:
 * Thailand costs roughly double Singapore, so blending it into one rate would either overcharge
 * Singapore or undercharge Thailand. Splitting it is both more honest and easier to defend.
 */
export type QuoteLineGroup = "fixed" | "usage" | "carrier";

export const QUOTE_LINE_GROUPS: { key: QuoteLineGroup; title: string; note: string }[] = [
	{ key: "fixed", title: "One-off — fixed", note: "Firm. Billed at completion." },
	{
		key: "usage",
		title: "AdaptiveX call time — estimated",
		note: "Charged per second from the moment a call connects.",
	},
	{
		key: "carrier",
		title: "Carrier charges — estimated · passed through at cost",
		note: "Charged by the telco carrying each call. Rates differ by market.",
	},
];

/**
 * What a client is told when we cannot price their request yet.
 *
 * Every refusal `priceRequest()` can raise is ours: a rate nobody has confirmed, a rate nobody
 * has set, a price book that disagrees with itself about currency. None of them is anything the
 * client can act on, and none of them is anything they should have to read — "these rates are
 * still placeholders" is an internal sentence about our own housekeeping. `autoQuote()` swallows
 * the refusal for exactly that reason and leaves the request in `submitted`, which the admin
 * queue reads as "we are holding this up"; this is the other half of that decision, and it is one
 * string so the wizard, the commercials step and the request page cannot describe the same
 * situation three different ways.
 *
 * It promises contact rather than a deadline. We committed to no SLA (UX §14 note 2), and a
 * screen that invents "within 24 hours" makes a promise nobody agreed to.
 */
export const PRICING_PENDING_CLIENT_COPY =
	"We're finalising your pricing. Nothing more is needed from you — we'll be in touch as soon as your quote is ready, and it will appear here and on your request the moment it is.";

/**
 * Carrier is asked about before variability, because a carrier line is variable too — testing
 * `isVariable` first would fold the pass-through back into our own call time.
 */
export function quoteLineGroup(line: Pick<QuoteLine, "code" | "isVariable">): QuoteLineGroup {
	if (line.code === "carrier_rate") return "carrier";
	return line.isVariable ? "usage" : "fixed";
}

// ---------------------------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------------------------

export const DOCUMENT_KIND_LABELS: Record<DocumentKind, string> = {
	call_flow: "Call flow",
	revised_call_flow: "Revised call flow",
	lead_list: "Lead list",
	sow: "Statement of work",
	po: "Purchase order",
	// "Suppression list" throughout, matching step 6: "do-not-call register" is ours and is never
	// uploaded, and one file called two names across two screens reads as two files.
	dnc_list: "Suppression list",
	other: "Other",
};

export const DOCUMENT_KIND_HELP: Record<DocumentKind, string> = {
	call_flow: "The script we build a new agent from.",
	revised_call_flow: "A rework of an agent that already exists.",
	lead_list: "Contacts to call. We clean them, split them by market and hand back what we dropped.",
	sow: "The statement of work in force for this request.",
	po: "Your purchase order, so the charge lands on your own reference.",
	dnc_list: "Numbers of your own we must never call. Applied across every market.",
	other: "Anything else you want us to have.",
};

export const DOCUMENT_KIND_COLORS: Record<DocumentKind, ChipColor> = {
	call_flow: "primary",
	revised_call_flow: "primary",
	lead_list: "info",
	sow: "secondary",
	po: "secondary",
	dnc_list: "warning",
	other: "default",
};

/**
 * What the file picker offers per kind. The API sniffs the bytes rather than trusting the
 * declared type, so this narrows the dialog and never decides anything.
 */
export const DOCUMENT_KIND_ACCEPT: Record<DocumentKind, string> = {
	call_flow: ".pdf,.docx,.txt",
	revised_call_flow: ".pdf,.docx,.txt",
	lead_list: ".csv",
	sow: ".pdf,.doc,.docx",
	po: ".pdf,.doc,.docx",
	dnc_list: ".csv",
	other: ".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt",
};

export const DOCUMENT_KIND_OPTIONS = Object.entries(DOCUMENT_KIND_LABELS).map(([value, label]) => ({
	value: value as DocumentKind,
	label,
}));

/** The API's per-document ceiling, checked here only so an oversized file fails before the upload. */
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;

export const OUTCOME_SOURCE_LABELS: Record<OutcomeSource, string> = {
	starter: "Standard set",
	call_flow: "From your call flow",
	previous: "From a previous campaign",
	client: "Added by you",
};

/**
 * The dispositions the platform records for itself, as one uneditable line on step 5 (UX §08).
 *
 * Not read off `request.outcomes`, and it cannot be: `@no_answer`, `@busy`, `@machine` and
 * `@opt_out` are seeded onto every *campaign* by the engine and carry the exact names the dialer
 * writes, so a copy of them on the request would be a client-editable definition of a row the
 * dialer owns — a renamed one is silently dropped by the stats query, which is how a market comes
 * to look like it underperformed. The `DropRequestSystemOutcomes` migration deleted every one of
 * those copies, so filtering the request's own outcomes for `isSystem` now always finds nothing.
 *
 * This is the line the client reads instead: the platform's own list, named the way they would say
 * it, and stated as ours rather than theirs.
 */
export const SYSTEM_DISPOSITIONS: { name: string; label: string }[] = [
	{ name: "@no_answer", label: "No answer" },
	{ name: "@busy", label: "Busy" },
	{ name: "@machine", label: "Voicemail" },
	{ name: "@opt_out", label: "Do not call" },
];

/** "No answer · Busy · Voicemail · Do not call" — the row exactly as UX §08 writes it. */
export const SYSTEM_DISPOSITION_LINE = SYSTEM_DISPOSITIONS.map(({ label }) => label).join(" · ");

/**
 * The conversion email as UX §08 specifies it, before the client changes anything.
 *
 * Placeholders are not defaults: an empty To sends nothing, and nothing visibly fails when it
 * does. These three are the whole design of the step — note 6: one template for the campaign,
 * differentiated by `{{lead__disposition}}` — so they arrive filled in and are edited, not
 * authored.
 */
export const DEFAULT_NOTIFICATION_TEMPLATES = {
	toTemplate: "{{contact__sales_rep}}",
	bccTemplate: "{{contact__sales_manager}}",
	subjectTemplate: "{{campaign__title}} — {{lead__disposition}}: {{contact__company}}",
} as const;

// ---------------------------------------------------------------------------------------------
// The lifecycle, mirrored
// ---------------------------------------------------------------------------------------------

/**
 * Client-side copy of `REQUEST_TRANSITIONS` so a button for an illegal move is disabled rather
 * than offered and then refused. The API remains the authority: anything it refuses still
 * surfaces as a toast.
 */
export const LEGAL_TRANSITIONS: Record<RequestState, RequestState[]> = {
	draft: ["submitted", "cancelled"],
	submitted: ["quoted", "changes_requested", "cancelled"],
	quoted: ["awaiting_confirmation", "changes_requested", "cancelled"],
	awaiting_confirmation: ["confirmed", "changes_requested", "cancelled"],
	// Once confirmed the rates are frozen; a change has to go back and be re-quoted.
	confirmed: ["in_preparation", "changes_requested", "cancelled"],
	in_preparation: ["ready", "changes_requested", "cancelled"],
	ready: ["in_preparation", "running", "cancelled"],
	running: ["completed", "cancelled"],
	completed: [],
	changes_requested: ["quoted", "cancelled"],
	cancelled: [],
};

export function canTransition(from: RequestState, to: RequestState): boolean {
	return LEGAL_TRANSITIONS[from]?.includes(to) ?? false;
}

/**
 * States in which the commercial terms are settled and the API refuses an edit, mirroring
 * `FROZEN_STATES`. Everything up to confirmation — including submitted and quoted — is still
 * editable; re-pricing after an edit is the normal path, not an exception.
 */
const FROZEN_STATES = new Set<RequestState>([
	"confirmed",
	"in_preparation",
	"ready",
	"running",
	"completed",
	"cancelled",
]);

export function isFrozen(state: RequestState): boolean {
	return FROZEN_STATES.has(state);
}

/** The states `POST /:request/confirm` accepts; it walks the rest of the way itself. */
const CONFIRMABLE_STATES = new Set<RequestState>(["quoted", "awaiting_confirmation"]);

export function canConfirm(state: RequestState): boolean {
	return CONFIRMABLE_STATES.has(state);
}

// ---------------------------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------------------------

/**
 * A decimal string as a number, or null where there is nothing to read.
 *
 * The one place `Number()` is allowed to be called on money. Every money column on a quote is
 * numeric and arrives as a string, and `Number(null)` is 0 while `Number(undefined)` is NaN —
 * which is how an absent variable total became the literal text "S$NaN" on the estimate tile.
 * Null out means "not answered", and the caller has to decide what to print; it can no longer be
 * mistaken for zero by arithmetic that did not stop to look.
 */
export function amountOrNull(value: string | number | null | undefined): number | null {
	if (value === null || value === undefined || value === "") return null;

	const amount = Number(value);
	return Number.isFinite(amount) ? amount : null;
}

/**
 * The bottom line of a quote: the firm part plus the estimated band around it.
 *
 * Null where the fixed total cannot be read, and that is deliberate rather than defensive. A
 * total that silently drops a half it could not parse is worse than no total — it is a smaller
 * number, presented with the same confidence, next to a signature box. An em dash sends someone
 * to look; "S$ 620 – 925" where the real answer was "S$ 3,195 – 3,525" does not.
 *
 * A quote with no variable lines is a firm total, not a missing one: the band collapses onto the
 * fixed figure and {@link formatMoneyRange} prints it once rather than as a range of one value.
 */
export function quoteTotalRange(
	quote:
		| {
				fixedTotal?: string | number | null;
				variableLow?: string | number | null;
				variableHigh?: string | number | null;
		  }
		| null
		| undefined
): { low: number; high: number } | null {
	const fixed = amountOrNull(quote?.fixedTotal);
	if (fixed === null) return null;

	const low = amountOrNull(quote?.variableLow);
	const high = amountOrNull(quote?.variableHigh);

	return { low: fixed + (low ?? 0), high: fixed + (high ?? low ?? 0) };
}

/**
 * Money arrives as a decimal string, so coerce once here rather than trusting a type. Everything
 * downstream — including the fallback — then works on a number, so an unrecognised currency code
 * degrades to "SGD 1200.00" instead of throwing.
 */
export function formatMoney(value: string | number | null | undefined, currency = "SGD"): string {
	const amount = amountOrNull(value);
	if (amount === null) return "—";

	try {
		return new Intl.NumberFormat("en-SG", { style: "currency", currency, maximumFractionDigits: 2 }).format(amount);
	} catch {
		// An unknown or malformed currency code must not blank out the whole quote.
		return `${currency} ${amount.toFixed(2)}`;
	}
}

/** A low–high band, which is how every estimated line on a quote is read. */
export function formatMoneyRange(
	low: string | number | null | undefined,
	high: string | number | null | undefined,
	currency = "SGD"
): string {
	const from = formatMoney(low, currency);
	const to = formatMoney(high, currency);

	if (from === "—" && to === "—") return "—";
	if (from === to) return from;
	return `${from} – ${to}`;
}

/** Quantities are numeric columns too: "3.0000" is three, and should read as three. */
export function formatQuantity(value: string | number | null | undefined): string {
	if (value === null || value === undefined || value === "") return "—";

	const quantity = Number(value);
	if (!Number.isFinite(quantity)) return String(value);

	return quantity.toLocaleString("en-SG", { maximumFractionDigits: 2 });
}

export function formatPercent(value: string | number | null | undefined): string {
	if (value === null || value === undefined || value === "") return "—";

	const percent = Number(value);
	if (!Number.isFinite(percent)) return "—";

	return `${percent.toLocaleString("en-SG", { maximumFractionDigits: 2 })}%`;
}

export function formatDate(value?: string | null): string {
	if (!value) return "—";

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "—";

	return date.toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });
}

export function formatDateTime(value?: string | null): string {
	if (!value) return "—";

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "—";

	return date.toLocaleString("en-GB", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

/**
 * A dial window as one phrase — "10 – 24 Aug 2026", collapsing the month and year when both ends
 * share them. A half-set range still reads: an end date with no start is "until 24 Aug 2026".
 */
export function formatDateRange(start?: string | null, end?: string | null): string {
	if (!start && !end) return "—";
	if (start && !end) return `from ${formatDate(start)}`;
	if (!start && end) return `until ${formatDate(end)}`;

	const from = new Date(start as string);
	const to = new Date(end as string);

	if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return `${formatDate(start)} – ${formatDate(end)}`;

	const sameYear = from.getFullYear() === to.getFullYear();
	const sameMonth = sameYear && from.getMonth() === to.getMonth();

	const head = sameMonth
		? from.toLocaleDateString("en-GB", { day: "numeric" })
		: from.toLocaleDateString("en-GB", { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });

	return `${head} – ${formatDate(end)}`;
}

/** Postgres hands back `HH:MM:SS`; a calling window reads as `HH:MM`. */
export function formatWallClock(value?: string | null): string {
	if (!value) return "—";
	return value.slice(0, 5);
}

export const WEEKDAYS: { value: number; label: string; short: string }[] = [
	{ value: 1, label: "Monday", short: "Mon" },
	{ value: 2, label: "Tuesday", short: "Tue" },
	{ value: 3, label: "Wednesday", short: "Wed" },
	{ value: 4, label: "Thursday", short: "Thu" },
	{ value: 5, label: "Friday", short: "Fri" },
	{ value: 6, label: "Saturday", short: "Sat" },
	{ value: 7, label: "Sunday", short: "Sun" },
];

/**
 * ISO-8601 weekday for a date, Monday is 1.
 *
 * Derived rather than taken from a plugin: dayjs's own `day()` counts Sunday as zero, and the
 * `isoWeek` plugin is not loaded in `@/lib/dayjs`. Every weekday number that crosses the API — the
 * calling days on a market row, the guidance flag's weak and strong days — is ISO, so converting
 * once here is what keeps a Sunday from being read as a Monday.
 */
export function isoWeekdayOf(date: { day: () => number }): number {
	const day = date.day();
	return day === 0 ? 7 : day;
}

export function weekdayName(iso: number | undefined): string {
	return WEEKDAYS.find((weekday) => weekday.value === iso)?.label ?? "—";
}

export function weekdayShort(iso: number | undefined): string {
	return WEEKDAYS.find((weekday) => weekday.value === iso)?.short ?? "—";
}

/**
 * ISO-8601 weekday numbers as a phrase.
 *
 * Empty reads as "Every day" because that is what an empty list provisions as — provisioning
 * expands it to all seven. No new market row can be written with one: the API refuses it and the
 * wizard says so beside the toggles, precisely because "every day" is nobody's idea of what
 * unticking every day meant. Rows written before that rule existed still render honestly here.
 */
export function formatCallingDays(days?: number[] | null): string {
	if (!days || days.length === 0) return "Every day";

	const sorted = days.toSorted((left, right) => left - right);
	const isWeekdays = sorted.length === 5 && sorted.every((day, index) => day === index + 1);
	if (isWeekdays) return "Mon – Fri";

	return sorted.map((day) => WEEKDAYS.find((weekday) => weekday.value === day)?.short ?? String(day)).join(" · ");
}

/**
 * Oldest first — the one order the transition log may be read in, on every screen that shows it.
 *
 * `at` alone is not an order. `confirm()` writes the confirmation and the move into preparation
 * inside one transaction, and provisioning writes another two, and `now()` is the transaction
 * clock rather than the statement's — so those rows carry an identical timestamp however fine the
 * column is, and sorting on it leaves their order to whatever the query plan returned. `seq` is
 * the database's monotonic write order and exists to break exactly that tie.
 *
 * Compared as a number because it crosses the wire as a decimal string, like every numeric column
 * on this API: `"9".localeCompare("10")` puts nine after ten.
 */
export function compareEventOrder(left: CampaignRequestEvent, right: CampaignRequestEvent): number {
	return left.at === right.at ? Number(left.seq) - Number(right.seq) : left.at.localeCompare(right.at);
}

/** How long a queue row has been sitting. Seconds in, the queue's own "4h" / "2d" out. */
export function formatAge(seconds: number | null | undefined): string {
	if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return "—";
	if (seconds < 60) return "just now";

	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m`;

	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h`;

	return `${Math.floor(hours / 24)}d`;
}

export function formatBytes(bytes: number | null | undefined): string {
	if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return "—";
	if (bytes < 1024) return `${bytes} B`;

	const units = ["KB", "MB", "GB"];
	let value = bytes / 1024;
	let unit = 0;

	while (value >= 1024 && unit < units.length - 1) {
		value /= 1024;
		unit += 1;
	}

	return `${value.toLocaleString("en-SG", { maximumFractionDigits: 1 })} ${units[unit]}`;
}

/** The keys the API copies onto a quote, in the order they make sense to read. */
const ASSUMPTION_LABELS: Record<string, string> = {
	expectedContacts: "Contacts to call",
	expectedReachRate: "Reach rate",
	expectedCallMinutes: "Average call length",
	estimatedMinutes: "Estimated call minutes",
	// A chip label is a tag, not a sentence: "Range either side of the estimate: ±15%" was a
	// clause wrapped in a pill on the quote and on the confirmation screen.
	variance: "Estimate range",
};

const ASSUMPTION_ORDER = Object.keys(ASSUMPTION_LABELS);

function assumptionValue(key: string, value: unknown): string {
	if (value === null || value === undefined || value === "") return "—";

	const numeric = Number(value);
	const hasNumber = typeof value !== "boolean" && Number.isFinite(numeric);

	if (hasNumber) {
		if (key === "expectedReachRate") return formatPercent(numeric);
		if (key === "variance") return `±${(numeric * 100).toLocaleString("en-SG", { maximumFractionDigits: 2 })}%`;
		if (key === "expectedCallMinutes" || key === "estimatedMinutes") {
			return `${numeric.toLocaleString("en-SG", { maximumFractionDigits: 2 })} min`;
		}
		return numeric.toLocaleString("en-SG", { maximumFractionDigits: 2 });
	}

	return typeof value === "object" ? JSON.stringify(value) : String(value);
}

/**
 * The assumptions a quote was built from, as label/value pairs a client can read. Publishing them
 * is what prevents the "why is this more than the quote" conversation — they can see exactly which
 * assumption moved.
 *
 * Unknown keys are kept rather than dropped: a figure the price depends on must never go
 * unpublished just because this file has not heard of it yet.
 */
export function quoteAssumptions(assumptions: Record<string, unknown> | null | undefined): {
	key: string;
	label: string;
	value: string;
}[] {
	if (!assumptions || typeof assumptions !== "object") return [];

	const keys = Object.keys(assumptions).toSorted((a, b) => {
		const left = ASSUMPTION_ORDER.indexOf(a);
		const right = ASSUMPTION_ORDER.indexOf(b);
		return (left === -1 ? ASSUMPTION_ORDER.length : left) - (right === -1 ? ASSUMPTION_ORDER.length : right);
	});

	return keys.map((key) => ({
		key,
		label: ASSUMPTION_LABELS[key] ?? key,
		value: assumptionValue(key, assumptions[key]),
	}));
}
